import Vue from "vue";
import VueRouter from "vue-router";

import i18n from "../plugins/i18n.js";
import {
  authPermissions,
  authRole,
  authUser,
  hasPermission,
  isAuthenticatedRole,
} from "@/app/user-module/helpers/authHelper";
import { getSavedLanguage } from "@/app/core-module/util/languageHelper";

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    redirect: `/${i18n.locale}/`,
  },
  {
    path: "/:lang",
    redirect: `/${i18n.locale}/home`,
  },
  {
    path: "/:lang",
    component: {
      render: h => h("router-view"),
    },
    children: [
      {
        path: "home",
        name: "Home",
        meta: {
          requiresLogin: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "Home" */ "../app/core-module/pages/HomePage"
          ),
      },
      {
        path: "sign-in",
        name: "SignIn",
        meta: {
          redirectAuthUser: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "SignIn" */ "../app/user-module/pages/SignInPage"
          ),
      },
      // {
      //   path: "sign-up",
      //   name: "SignUp",
      //   meta: { redirectAuthUser: true },
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "SignUp" */ "../app/user-module/pages/SignUpPage"
      //     ),
      // },
      {
        path: "forgot-password",
        name: "ForgotPassword",
        component: () =>
          import(
            /* webpackChunkName: "ForgotPassword" */ "../app/user-module/pages/ForgotPasswordPage"
          ),
      },
      // {
      //   path: "profile",
      //   name: "Profile",
      //   meta: { requiresLogin: true },
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "Profile" */ "../app/user-module/pages/ProfilePage"
      //     ),
      // },
      {
        path: "change-password",
        name: "ChangePassword",
        meta: {
          requiresLogin: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "ChangePassword" */ "../app/user-module/pages/ChangePasswordPage"
          ),
      },
      {
        path: "active-sessions",
        name: "ActiveSessions",
        meta: {
          requiresLogin: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "ActiveSessions" */ "../app/user-module/pages/ActiveSessionsPage"
          ),
      },
      {
        path: "login-audit",
        name: "LoginAudit",
        meta: {
          requiresLogin: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "LoginAudit" */ "../app/user-module/pages/LoginAuditPage"
          ),
      },
      {
        path: "courses",
        name: "Courses",
        meta: {
          requiresLogin: true,
          restrictOrganizationManager: true,
          requiredPermissions: ["GET_TRAININGS"],
        },
        component: () =>
          import(
            /* webpackChunkName: "Courses" */ "../app/course-module/pages/CoursesPage.vue"
          ),
      },
      {
        path: "course-details/:trainingId?",
        name: "CourseDetails",
        meta: {
          requiresLogin: true,
          restrictOrganizationManager: true,
          requiredPermissions: [
            "GET_TRAININGS",
            "CREATE_TRAINING",
            "UPDATE_TRAINING",
          ],
          requiredAll: false,
        },
        component: () =>
          import(
            /* webpackChunkName: "CourseDetails" */ "../app/course-module/pages/CourseDetailsPage"
          ),
      },
      {
        path: "quizzes",
        name: "Quizzes",
        meta: {
          requiresLogin: true,
          restrictOrganizationManager: true,
          requiredPermissions: ["GET_QUIZZES"],
        },
        component: () =>
          import(
            /* webpackChunkName: "Quizzes" */ "../app/quiz-module/pages/QuizzesPage.vue"
          ),
      },
      {
        path: "quiz-details/:quizId?",
        name: "QuizDetails",
        meta: {
          requiresLogin: true,
          restrictOrganizationManager: true,
          requiredPermissions: ["GET_QUIZZES", "CREATE_QUIZ", "UPDATE_QUIZ"],
          requiredAll: false,
        },
        component: () =>
          import(
            /* webpackChunkName: "QuizDetails" */ "../app/quiz-module/pages/QuizDetailsPage"
          ),
      },
      {
        path: "registrations",
        name: "Registrations",
        meta: {
          requiresLogin: true,
          requiredPermissions: ["GET_TRAINING_REGISTRATIONS"],
        },
        component: () =>
          import(
            /* webpackChunkName: "Registrations" */ "../app/user-module/pages/Registrations.vue"
          ),
      },
      {
        path: "registration",
        name: "Registration",
        meta: {
          requiresLogin: true,
          restrictOrganizationManager: true,
          requiredPermissions: ["CREATE_TRAINING_REGISTRATION"],
        },
        component: () =>
          import(
            /* webpackChunkName: "Registration" */ "../app/user-module/pages/Registration.vue"
          ),
      },
      {
        path: "reports",
        name: "Reports",
        meta: {
          requiresLogin: true,
          requiredPermissions: [
            "GET_REGISTRATION_REPORTS",
            "GET_REVENUE_REPORTS",
            "GET_QUIZ_REPORTS",
            "GET_ASSESSMENT_REPORTS",
            "GET_FEEDBACK_REPORTS",
            "GET_TRAINING_MATRIX_REPORTS",
            "GET_BATCH_REPORTS",
          ],
          requiredAll: false,
        },
        component: () =>
          import(
            /* webpackChunkName: "Reports" */ "../app/reports-module/pages/Reports.vue"
          ),
      },
      {
        path: "certificates",
        name: "Certificates",
        meta: {
          requiresLogin: true,
          requiredPermissions: [
            "DOWNLOAD_CERTIFICATE",
            "DOWNLOAD_PROFILE_CARD",
          ],
          requiredAll: false,
        },
        component: () =>
          import(
            /* webpackChunkName: "Certificates" */ "../app/user-module/pages/CertificatesPage"
          ),
      },
      {
        path: "invoices",
        name: "Invoices",
        meta: {
          requiresLogin: true,
          restrictOrganizationManager: true,
          requiredPermissions: ["GENERATE_INVOICE", "DOWNLOAD_INVOICE"],
          requiredAll: false,
        },
        component: () =>
          import(
            /* webpackChunkName: "Invoices" */ "../app/invoices-module/pages/InvoicesPage"
          ),
      },
      {
        path: "employees-roles",
        name: "EmployeesRoles",
        meta: {
          requiresLogin: true,
          requiredPermissions: ["GET_EMPLOYEES"],
        },
        component: () =>
          import(
            /* webpackChunkName: "EmployeesRoles" */ "../app/user-module/pages/EmployeesRolesPage"
          ),
      },
      {
        path: "notifications",
        name: "Notifications",
        meta: {
          requiresLogin: true,
          restrictOrganizationManager: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "Notifications" */ "../app/core-module/pages/NotificationsPage"
          ),
      },
      {
        path: "organizations",
        name: "Organizations",
        meta: {
          requiresLogin: true,
          restrictOrganizationManager: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "Organizations" */ "../app/organization-module/pages/Organizations.vue"
          ),
      },
      {
        path: "categories",
        name: "Categories",
        meta: {
          requiresLogin: true,
          restrictOrganizationManager: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "Categories" */ "../app/category-module/pages/CategoriesPage"
          ),
      },
      {
        path: "employee-profile/:userId?",
        name: "EmployeeProfile",
        meta: {
          requiresLogin: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "EmployeeProfile" */ "../app/user-module/pages/EmployeeProfilePage.vue"
          ),
      },
      {
        path: "employee-basic-profile/:userId?",
        name: "EmployeeBasicProfile",
        meta: {
          requiresLogin: true,
        },
        component: () =>
          import("../app/user-module/pages/EmployeeProfilePage.vue"),
      },
      {
        path: "organization-profile/:organizationId?",
        name: "OrganizationProfile",
        meta: {
          requiresLogin: true,
          restrictOrganizationManager: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "OrganizationProfile" */ "../app/organization-module/pages/OrganizationProfile.vue"
          ),
      },
      {
        path: "settings",
        name: "Settings",
        meta: {
          requiresLogin: true,
          restrictOrganizationManager: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "../app/user-module/pages/CompanyProfilePage.vue"
          ),
      },
      {
        path: "attendance",
        name: "Attendance",
        meta: {
          requiresLogin: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "../app/user-module/pages/AttendanceDownloadPage.vue"
          ),
      },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  let language = getSavedLanguage() || to.params.lang;
  if (!language) language = "en";
  i18n.locale = language;

  const isAuthUser = isAuthenticatedRole(authRole());
  const restrictOrganizationManager =
    to.meta?.restrictOrganizationManager && authUser()?.isOrganizationManager;

  if (to.meta.requiresLogin && !isAuthUser) {
    next({ name: "SignIn", params: { lang: i18n.locale } });
  } else if (to.meta.redirectAuthUser && isAuthUser) {
    next({ name: "Home", params: { lang: i18n.locale } });
  } else if (
    (restrictOrganizationManager || to.meta.requiredPermissions) &&
    isAuthUser
  ) {
    const currentRole = authRole();
    const currentPermissions = authPermissions();

    const hasNoPermission = !hasPermission({
      currentRole,
      currentPermissions,
      requiredPermission: to.meta.requiredPermissions,
      requiredAll: to.meta.requiredAll,
      restrictOrganizationManager,
    });

    const hasNoPermissionForCourseDetails =
      (to.name === "CourseDetails" &&
        !to.params.trainingId &&
        !currentPermissions.includes("CREATE_TRAINING")) ||
      (to.name === "CourseDetails" &&
        to.params.trainingId &&
        !currentPermissions.includes("UPDATE_TRAINING") &&
        !currentPermissions.includes("GET_TRAININGS"));

    if (
      hasNoPermission ||
      (currentRole !== "ADMIN" && hasNoPermissionForCourseDetails)
    ) {
      if (from.name === null) {
        next({ name: "Home", params: { lang: i18n.locale } });
      } else if (from.name !== "Home") {
        next({ name: "Home", params: { lang: i18n.locale } });
      }
    } else {
      next();
    }
  } else {
    next();
  }
});

export default router;
